import { throttle } from 'lodash-es';
import React, { useCallback, useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { makeStyles } from 'tss-react/mui';
import { isTouchDevice, shouldEnableResize } from '../../../base/environment/utils.web';
import { closeAllSidePanels, setSidePanelIsResizing, setUserSidePanelWidth } from '../../actions';
import { DEFAULT_SIDE_PANEL_WIDTH, MIN_SIDE_PANEL_WIDTH, SIDE_PANEL_DRAG_HANDLE_HEIGHT, SIDE_PANEL_DRAG_HANDLE_OFFSET, SIDE_PANEL_DRAG_HANDLE_WIDTH, SIDE_PANEL_MOBILE_DRAWER_BACKDROP_OPACITY, SIDE_PANEL_MOBILE_DRAWER_HANDLE_HEIGHT_PX, SIDE_PANEL_MOBILE_DRAWER_HANDLE_PADDING_BOTTOM_PX, SIDE_PANEL_MOBILE_DRAWER_HANDLE_PADDING_TOP_PX, SIDE_PANEL_MOBILE_DRAWER_HANDLE_WIDTH_PX, SIDE_PANEL_MOBILE_DRAWER_HEIGHT, SIDE_PANEL_MOBILE_DRAWER_RADIUS_PX, SIDE_PANEL_MOBILE_DRAWER_Z_INDEX, SIDE_PANEL_POPOUT_MIN_WIDTH, SIDE_PANEL_TOUCH_HANDLE_SIZE } from '../../constants';
import { getSidePanelMaxSize, getSidePanelState, isSidePanelOpen, shouldUseSidePanelBottomDrawer } from '../../functions';
import logger from '../../logger';
import SidePanelTabBar from './SidePanelTabBar';
import SidePanelTabContent from './SidePanelTabContent';
const useStyles = makeStyles()((theme, { isResizing, isTouch, resizeEnabled, useBottomDrawer, width }) => {
    return {
        backdrop: {
            backgroundColor: `rgba(0, 0, 0, ${SIDE_PANEL_MOBILE_DRAWER_BACKDROP_OPACITY})`,
            bottom: 0,
            left: 0,
            position: 'fixed',
            right: 0,
            top: 0,
            zIndex: SIDE_PANEL_MOBILE_DRAWER_Z_INDEX - 1
        },
        host: {
            backgroundColor: theme.palette.ui01,
            borderLeft: useBottomDrawer ? 'none' : `1px solid ${theme.palette.ui04}`,
            borderRadius: useBottomDrawer
                ? `${SIDE_PANEL_MOBILE_DRAWER_RADIUS_PX}px ${SIDE_PANEL_MOBILE_DRAWER_RADIUS_PX}px 0 0`
                : undefined,
            bottom: useBottomDrawer ? 0 : undefined,
            boxSizing: 'border-box',
            display: 'flex',
            flexDirection: 'column',
            flexShrink: 0,
            height: useBottomDrawer ? SIDE_PANEL_MOBILE_DRAWER_HEIGHT : '100%',
            left: useBottomDrawer ? 0 : undefined,
            marginBottom: useBottomDrawer ? 'env(safe-area-inset-bottom, 0)' : undefined,
            overflow: 'hidden',
            position: useBottomDrawer ? 'fixed' : 'relative',
            right: useBottomDrawer ? 0 : undefined,
            top: useBottomDrawer ? 'auto' : undefined,
            transition: isResizing || useBottomDrawer ? undefined : 'width .16s ease-in-out',
            width: useBottomDrawer ? '100%' : `${width}px`,
            zIndex: useBottomDrawer ? SIDE_PANEL_MOBILE_DRAWER_Z_INDEX : 300,
            ...(!isTouch && !useBottomDrawer && {
                '&:hover, &:focus-within': {
                    '& .sidePanelDragHandleContainer': {
                        visibility: 'visible'
                    }
                }
            }),
            // 电脑窄屏：保持原全屏覆盖，不用底部抽屉。
            ...(!useBottomDrawer && {
                [`@media (max-width: ${SIDE_PANEL_POPOUT_MIN_WIDTH}px)`]: {
                    height: '100dvh',
                    left: 0,
                    position: 'fixed',
                    right: 0,
                    top: 0,
                    width: '100%',
                    zIndex: SIDE_PANEL_MOBILE_DRAWER_Z_INDEX
                }
            })
        },
        grabHandle: {
            alignItems: 'center',
            display: useBottomDrawer ? 'flex' : 'none',
            flexShrink: 0,
            justifyContent: 'center',
            paddingTop: `${SIDE_PANEL_MOBILE_DRAWER_HANDLE_PADDING_TOP_PX}px`,
            paddingBottom: `${SIDE_PANEL_MOBILE_DRAWER_HANDLE_PADDING_BOTTOM_PX}px`
        },
        grabHandleBar: {
            backgroundColor: theme.palette.ui04,
            borderRadius: `${SIDE_PANEL_MOBILE_DRAWER_HANDLE_HEIGHT_PX}px`,
            height: `${SIDE_PANEL_MOBILE_DRAWER_HANDLE_HEIGHT_PX}px`,
            width: `${SIDE_PANEL_MOBILE_DRAWER_HANDLE_WIDTH_PX}px`
        },
        content: {
            display: 'flex',
            flex: 1,
            flexDirection: 'column',
            minHeight: 0,
            overflow: 'hidden',
            position: 'relative'
        },
        panel: {
            display: 'flex',
            flex: 1,
            flexDirection: 'column',
            height: '100%',
            minHeight: 0,
            overflow: 'hidden',
            '&.hide': {
                display: 'none'
            }
        },
        dragHandleContainer: {
            alignItems: 'center',
            backgroundColor: 'transparent',
            cursor: 'col-resize',
            display: resizeEnabled && !useBottomDrawer ? 'flex' : 'none',
            height: '100%',
            justifyContent: 'center',
            left: isTouch
                ? `${SIDE_PANEL_DRAG_HANDLE_OFFSET
                    - Math.floor((SIDE_PANEL_TOUCH_HANDLE_SIZE - SIDE_PANEL_DRAG_HANDLE_WIDTH) / 2)}px`
                : `${SIDE_PANEL_DRAG_HANDLE_OFFSET}px`,
            position: 'absolute',
            top: 0,
            touchAction: 'none',
            visibility: isTouch && resizeEnabled ? 'visible' : 'hidden',
            width: isTouch ? `${SIDE_PANEL_TOUCH_HANDLE_SIZE}px` : `${SIDE_PANEL_DRAG_HANDLE_WIDTH}px`,
            zIndex: 2,
            '&:hover': {
                '& .sidePanelDragHandle': {
                    backgroundColor: theme.palette.icon01
                }
            },
            '&.visible': {
                visibility: 'visible',
                '& .sidePanelDragHandle': {
                    backgroundColor: theme.palette.icon01
                }
            },
            [`@media (max-width: ${SIDE_PANEL_POPOUT_MIN_WIDTH}px)`]: {
                display: 'none'
            }
        },
        dragHandle: {
            backgroundColor: theme.palette.icon02,
            borderRadius: '1px',
            height: `${SIDE_PANEL_DRAG_HANDLE_HEIGHT}px`,
            width: `${SIDE_PANEL_DRAG_HANDLE_WIDTH / 3}px`,
            ...(isTouch && resizeEnabled && {
                backgroundColor: theme.palette.icon01
            })
        }
    };
});
/**
 * Right-side unified host for docked side panel apps.
 * 手机端改为底部抽屉；电脑 Web 保持右侧侧栏。
 *
 * @returns {JSX.Element | null}
 */
export default function SidePanelHost() {
    const dispatch = useDispatch();
    const open = useSelector(isSidePanelOpen);
    const { openTabs, activeTab, width, isResizing } = useSelector(getSidePanelState);
    const maxWidth = useSelector(getSidePanelMaxSize);
    const panelWidth = width?.current ?? DEFAULT_SIDE_PANEL_WIDTH;
    const isTouch = isTouchDevice();
    const resizeEnabled = shouldEnableResize();
    const useBottomDrawer = shouldUseSidePanelBottomDrawer();
    const { classes, cx } = useStyles({
        isResizing,
        isTouch,
        resizeEnabled,
        useBottomDrawer,
        width: panelWidth
    });
    const [isMouseDown, setIsMouseDown] = useState(false);
    const [mousePosition, setMousePosition] = useState(null);
    const [dragPanelWidth, setDragPanelWidth] = useState(null);
    const onDragHandlePointerDown = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        e.target.setPointerCapture(e.pointerId);
        setIsMouseDown(true);
        setMousePosition(e.clientX);
        setDragPanelWidth(panelWidth);
        dispatch(setSidePanelIsResizing(true));
        document.body.style.cursor = 'col-resize';
        document.body.style.userSelect = 'none';
    }, [dispatch, panelWidth]);
    const onDragPointerUp = useCallback(() => {
        if (!isMouseDown) {
            return;
        }
        setIsMouseDown(false);
        dispatch(setSidePanelIsResizing(false));
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
    }, [dispatch, isMouseDown]);
    const onPanelResize = useCallback(throttle((e) => {
        if (!isMouseDown || mousePosition === null || dragPanelWidth === null) {
            return;
        }
        const diff = e.clientX - mousePosition;
        const newWidth = Math.max(Math.min(dragPanelWidth - diff, maxWidth), MIN_SIDE_PANEL_WIDTH);
        if (newWidth !== panelWidth) {
            dispatch(setUserSidePanelWidth(newWidth));
        }
    }, 50, {
        leading: true,
        trailing: false
    }), [dispatch, dragPanelWidth, isMouseDown, maxWidth, mousePosition, panelWidth]);
    const onBackdropClick = useCallback(() => {
        logger.info('手机端底部抽屉点击遮罩关闭');
        dispatch(closeAllSidePanels());
    }, [dispatch]);
    const onDrawerClick = useCallback((event) => {
        event.stopPropagation();
    }, []);
    useEffect(() => {
        document.addEventListener('pointerup', onDragPointerUp);
        document.addEventListener('pointermove', onPanelResize);
        return () => {
            document.removeEventListener('pointerup', onDragPointerUp);
            document.removeEventListener('pointermove', onPanelResize);
        };
    }, [onDragPointerUp, onPanelResize]);
    useEffect(() => {
        if (open && useBottomDrawer) {
            logger.info('手机端侧栏以底部抽屉展示', {
                activeTab,
                openTabs
            });
        }
    }, [activeTab, open, openTabs, useBottomDrawer]);
    if (!open) {
        return null;
    }
    const panel = (React.createElement("aside", { className: classes.host, id: 'side-panel-host', onClick: useBottomDrawer ? onDrawerClick : undefined },
        React.createElement("div", { className: cx(classes.dragHandleContainer, (isMouseDown || isResizing) && 'visible', 'sidePanelDragHandleContainer'), onPointerDown: onDragHandlePointerDown },
            React.createElement("div", { className: cx(classes.dragHandle, 'sidePanelDragHandle') })),
        React.createElement("div", { "aria-hidden": true, className: classes.grabHandle },
            React.createElement("div", { className: classes.grabHandleBar })),
        React.createElement(SidePanelTabBar, null),
        React.createElement("div", { className: classes.content }, openTabs.map(tabId => (React.createElement("div", { className: cx(classes.panel, activeTab !== tabId && 'hide'), key: tabId },
            React.createElement(SidePanelTabContent, { surface: 'docked', tabId: tabId })))))));
    // 手机端底部抽屉挂到 body，避免占用会议区 flex 槽位。
    if (useBottomDrawer && typeof document !== 'undefined') {
        return ReactDOM.createPortal(React.createElement(React.Fragment, null,
            React.createElement("div", { "aria-hidden": true, className: classes.backdrop, onClick: onBackdropClick }),
            panel), document.body);
    }
    return panel;
}
